import React from 'react';
import clsx from 'classnames';

type TypographyProps = {
  variant?: keyof typeof variantClasses;
  className?: string;
  children: React.ReactNode;
  color?: 'primary' | 'secondary';
  font?: 'medium' | 'bold' | 'light';
  /** Override the rendered element, e.g. a section title as <h2>. */
  as?: React.ElementType;
};

/**
 * Two scales live here.
 *
 * `h1`–`caption` are the original ones. The player and ~45 admin callers use
 * them, so their classes are frozen.
 *
 * The `adm*` variants are the admin chrome's scale from the frames. They exist
 * so pages stop hand-typing `font-display text-3xl font-bold text-adm-ink`,
 * which had already drifted across eight section pages.
 */
const variantClasses = {
  h1: 'text-6xl font-bold',
  h2: 'text-4xl font-semibold',
  h3: 'text-3xl font-semibold',
  h4: 'text-2xl font-medium',
  h5: 'text-xl font-medium',
  h6: 'text-base font-medium',
  p: 'text-base',
  span: 'text-base',
  caption: 'text-sm ',

  /** The section you are on: "Modules", "Review", "Learners". */
  admPageTitle: 'font-display text-3xl font-bold text-adm-ink',
  /** The course above the section, and other secondary titles. */
  admCourseTitle: 'font-display text-2xl font-bold text-adm-ink',
  /** The frames' `.sect` — a heading over a block within a section. */
  admSectionTitle: 'font-display text-xl font-bold text-adm-ink',
  /** A card or panel heading. */
  admCardTitle: 'text-base font-semibold text-adm-ink',
  /** A row's primary line in a list. */
  admRowTitle: 'text-nav font-semibold text-adm-ink',
  /** Supporting detail: counts, timestamps, "6 modules · 84 learners". */
  admMeta: 'text-meta text-adm-ink3',
  /** Explanatory copy under a heading. */
  admBody: 'text-nav leading-relaxed text-adm-ink2',
};

/** Default element per admin variant; `as` overrides it. */
const variantElements: Partial<Record<keyof typeof variantClasses, React.ElementType>> = {
  admPageTitle: 'h1',
  admCourseTitle: 'h1',
  admSectionTitle: 'h2',
  admCardTitle: 'h2',
  admRowTitle: 'div',
  admMeta: 'p',
  admBody: 'p',
};

export const Typography: React.FC<TypographyProps> = ({
  variant = 'p',
  className,
  color,
  children,
  font,
  as,
  ...props
}) => {
  const Component = (as ?? variantElements[variant] ?? (variant as keyof JSX.IntrinsicElements)) as React.ElementType;

  return (
    <Component
      className={clsx(
        variantClasses[variant],
        {
          'text-primary': (props as any).color === 'primary',
          'text-secondary': (props as any).color === 'secondary',
        },
        {
          'howio-font-md': font === 'medium',
        },
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

export const H1 = (props: TypographyProps) => <Typography font="medium" variant="h1" {...props} />;
export const H2 = (props: TypographyProps) => <Typography font="medium" variant="h2" {...props} />;
export const H3 = (props: TypographyProps) => <Typography variant="h3" {...props} />;
export const H4 = (props: TypographyProps) => <Typography variant="h4" {...props} />;
export const H5 = (props: TypographyProps) => <Typography variant="h5" {...props} />;
export const H6 = (props: TypographyProps) => <Typography variant="h6" {...props} />;
export const P = (props: TypographyProps) => <Typography variant="p" {...props} />;
export const Span = (props: TypographyProps) => <Typography variant="span" {...props} />;
export const Caption = (props: TypographyProps) => <Typography variant="caption" {...props} />;

/* Admin chrome. */
export const PageTitle = (props: TypographyProps) => <Typography variant="admPageTitle" {...props} />;
export const CourseTitle = (props: TypographyProps) => <Typography variant="admCourseTitle" {...props} />;
export const SectionTitle = (props: TypographyProps) => <Typography variant="admSectionTitle" {...props} />;
export const CardTitle = (props: TypographyProps) => <Typography variant="admCardTitle" {...props} />;
export const RowTitle = (props: TypographyProps) => <Typography variant="admRowTitle" {...props} />;
export const Meta = (props: TypographyProps) => <Typography variant="admMeta" {...props} />;
export const Body = (props: TypographyProps) => <Typography variant="admBody" {...props} />;
